// Audit log viewer for admins. Filterable by kind / actor / subject / date, with CSV export.
// Source: every row in `audit_log` — added on login, magic-link issue, scenario publish/retire,
// MCQ attempts, CPD-PDF / MCQ-cert downloads, admin user-role changes, run end. Required for
// ISO/IEC and clinical-governance reporting.

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

const KIND_LABELS: Record<string, string> = {
  login: 'Login',
  magic_link_request: 'Magic link issued',
  scenario_publish: 'Scenario published',
  scenario_retire: 'Scenario retired',
  user_role_change: 'User role changed',
  user_status_change: 'User status changed',
  run_end: 'Run ended',
  mcq_attempt: 'MCQ attempt',
  mcq_certificate_pdf: 'MCQ certificate downloaded',
  cpd_evidence_pdf: 'CPD evidence downloaded',
  session_revoke: 'Session revoked',
};

export default function AuditLog() {
  const [audit, setAudit] = useState<any[] | null>(null);
  const [kind, setKind] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [days, setDays] = useState<number>(30);

  useEffect(() => { api.adminAudit().then((r) => setAudit(r.audit)).catch(() => setAudit([])); }, []);

  const filtered = useMemo(() => {
    if (!audit) return null;
    const cutoff = Date.now() - days * 86400_000;
    const q = search.trim().toLowerCase();
    return audit.filter((a) => {
      if (a.ts < cutoff) return false;
      if (kind && a.kind !== kind) return false;
      if (q && !(a.actor_user_id?.toLowerCase().includes(q) || a.subject_id?.toLowerCase().includes(q) || a.payload_summary?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [audit, kind, search, days]);

  const kindCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of audit || []) m.set(a.kind, (m.get(a.kind) || 0) + 1);
    return Array.from(m.entries()).sort((x, y) => y[1] - x[1]);
  }, [audit]);

  function exportCsv() {
    if (!filtered || filtered.length === 0) return;
    const rows = ['timestamp,kind,actor_user_id,subject_id,ip_hash,payload_summary'];
    for (const a of filtered) {
      const cells = [
        new Date(a.ts).toISOString(),
        a.kind || '',
        a.actor_user_id || '',
        a.subject_id || '',
        (a.ip_hash || '').slice(0, 16),
        (a.payload_summary || '').replace(/"/g, '""'),
      ].map((c) => `"${c}"`).join(',');
      rows.push(cells);
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Audit log</h1>
          <p className="text-mono-white/50 text-sm">Every admin action, login, run end, MCQ attempt, and PDF download. IP addresses are hashed; raw emails are never logged.</p>
        </div>
        <button onClick={exportCsv} disabled={!filtered || filtered.length === 0} className="btn-ghost text-sm disabled:opacity-30">⤓ Export CSV</button>
      </div>

      {/* Filter strip */}
      <div className="panel p-3 mb-4 grid sm:grid-cols-3 gap-3">
        <label className="text-xs">
          <span className="text-mono-white/40 uppercase tracking-widest">Event kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full bg-ink-800 border border-ink-800 rounded px-2 py-2 text-mono-white focus:border-brand-600 focus:outline-none text-sm">
            <option value="">All ({audit?.length || 0})</option>
            {kindCounts.map(([k, n]) => (
              <option key={k} value={k}>{KIND_LABELS[k] || k} ({n})</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="text-mono-white/40 uppercase tracking-widest">Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="user_id, subject_id, summary text…"
            className="mt-1 w-full bg-ink-800 border border-ink-800 rounded px-2 py-2 text-mono-white focus:border-brand-600 focus:outline-none text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="text-mono-white/40 uppercase tracking-widest">Last N days</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="mt-1 w-full bg-ink-800 border border-ink-800 rounded px-2 py-2 text-mono-white focus:border-brand-600 focus:outline-none text-sm">
            <option value={1}>Today</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
            <option value={99999}>All time</option>
          </select>
        </label>
      </div>

      {filtered == null && <p className="text-mono-white/60">Loading…</p>}
      {filtered && filtered.length === 0 && <p className="text-mono-white/60 panel p-4">No audit events match the filter.</p>}
      {filtered && filtered.length > 0 && (
        <>
          <p className="text-xs text-mono-white/40 mb-2 tabular-nums">{filtered.length} of {audit?.length || 0} events</p>
          <div className="panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-mono-white/40 bg-ink-800/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Kind</th>
                  <th className="text-left px-3 py-2 font-medium">Actor</th>
                  <th className="text-left px-3 py-2 font-medium">Subject</th>
                  <th className="text-left px-3 py-2 font-medium">IP (hash)</th>
                  <th className="text-left px-3 py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-ink-800 hover:bg-ink-800/30">
                    <td className="px-3 py-2 text-xs text-mono-white/70 font-mono whitespace-nowrap">{new Date(a.ts).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })}</td>
                    <td className="px-3 py-2"><span className="chip-off uppercase text-[10px] tracking-widest">{KIND_LABELS[a.kind] || a.kind}</span></td>
                    <td className="px-3 py-2 text-xs font-mono text-mono-white/60">{a.actor_user_id?.slice(0, 12) || '—'}{a.actor_user_id && '…'}</td>
                    <td className="px-3 py-2 text-xs font-mono text-mono-white/60">{a.subject_id?.slice(0, 12) || '—'}{a.subject_id && '…'}</td>
                    <td className="px-3 py-2 text-[10px] font-mono text-mono-white/40">{a.ip_hash?.slice(0, 10) || '—'}</td>
                    <td className="px-3 py-2 text-xs">{a.payload_summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
